import type { Fatturabilita } from '@/utils/fatturabilita'
import type { Disdetta } from '@/utils/disdetta'

// Service Manager TypeScript Interfaces

// Enums
export enum ServiceStatus {
  PENDING = 'pending',
  PARTIAL = 'partial',
  COMPLETE = 'complete',
  ARCHIVED = 'archived'
}

export enum ResourceState {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DELETED = 'deleted',
  UNKNOWN = 'unknown'
}

export enum UserRole {
  ADMIN = 'admin',
  TECH_USER = 'tech_user',
  BCO_USER = 'bco_user',
  UNAUTHORIZED = 'unauthorized'
}

export enum AssignmentType {
  MANUAL = 'manual',
  AUTOMATIC = 'automatic',
  RULESET = 'ruleset'
}

export enum ServiceCustomerType {
  CUSTOMER = 'customer',
  INTERNAL = 'internal',
  INTERCOMPANY = 'intercompany'
}

export enum CustomerType {
  INTERNAL_DEPT = 'internal_dept',
  EXTERNAL = 'external'
}

// Core Interfaces
export interface User {
  user_id: string
  google_sub?: string
  auth0_user_id?: string
  email: string
  name?: string
  avatar_url?: string
  role: string | UserRole
  is_disabled: boolean
  created_at: string
  updated_at?: string
  last_login_at?: string
  // Legacy fields maintained for partial compatibility
  username?: string
  active?: boolean
}

export interface Service {
  service_id: string
  service_handle: string
  name: string
  description: string | null
  customer_type: string | null
  customer_detail: string | null
  account_id: string | null
  // Dall'anagrafica, non dal servizio: chi è l'intestatario e se è del gruppo.
  account_name?: string | null
  account_type?: string | null
  template_id: string | null
  template_name: string | null
  completion_percentage: number
  status: ServiceStatus
  environment: string
  assignment_strategy: string
  ruleset_name: string | null
  region: string | null
  cost_center: string | null
  /** Centro di costo analitico (OPENSPEC_PRODOTTO_INFRAGRUPPO): vuoto finché la contabilità non lo comunica. */
  codice_prodotto?: string | null
  prodotto?: string | null
  /** Solo dopo una modifica: i mesi le cui righe hanno ricevuto il prodotto. */
  bonifica?: { periodi: string[] } | null
  contract_id?: string | null
  /** Il contratto collegato, come lo spediscono lista e dettaglio dei servizi.
   *  `contract_deleted` esiste perche' il legame sopravvive alla cancellazione
   *  logica del contratto: chi legge deve poterlo dire. */
  contract_code?: string | null
  contract_description?: string | null
  contract_status?: string | null
  contract_start_date?: string | null
  contract_end_date?: string | null
  contract_deleted?: boolean
  /** Esclusione manuale dalla fatturazione, propria e del contratto (OPENSPEC_NON_FATTURABILI). */
  fatturabilita?: Fatturabilita
  contract_fatturabilita?: Fatturabilita | null
  /** Disdetta (OPENSPEC_DISDETTE): la propria e quella del contratto. */
  disdetta?: Disdetta | null
  contract_disdetta?: Disdetta | null
  /** Solo nel dettaglio: risorse cessate ancora assegnate. */
  risorse_cessate?: number
  request_id?: string | null
  notes?: string | null
  tags: Record<string, unknown>
  is_active: boolean
  archived_at: string | null
  archived_by: string | null
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
  deleted_at: string | null
}

export interface Account {
  account_id: string
  customer_id: string // Included for compatibility
  name: string
  type: string
  legal_name?: string | null
  cost_center: string | null
  region: string | null
  contact_email: string | null
  contact_name: string | null
  notes: string | null
  active: boolean
  created_at: string
  updated_at: string
  
  // New account fields
  piva?: string | null
  vat_number?: string | null
  codice_fiscale?: string | null
  fiscal_code?: string | null
  commercial_ics?: string | null
  commercial_bsa?: string | null
  azienda_riferimento?: string | null
  ref_applicativo?: string | null
}

export type Customer = Account;

export interface Template {
  template_id: string
  /** How many live services were created from this template. */
  service_count?: number
  name: string
  description: string | null
  version: string
  resource_types: TemplateResourceType[]
  created_at: string
  created_by: string
}

export interface TemplateResourceType {
  resource_type_code: string
  name: string
  mandatory: boolean
  min_quantity: number
  max_quantity: number
}

export interface Assignment {
  assignment_id: string
  resource_id: string
  service_handle: string
  assignment_type: AssignmentType
  priority: number
  confidence_score: number | null
  resource_state: ResourceState
  resource_name: string | null
  last_seen_at: string | null
  state_changed_at: string | null
  inactive_reason: string | null
  active: boolean
  assigned_at: string
  assigned_by: string
  unassigned_at: string | null
  assignment_reason: string | null
}

// Resource Assignment Types
export interface Resource {
  resource_id: string
  name: string
  type: string  // Resource type code (e.g., 'vcpu', 'ram', 'server')
  state: ResourceState
  confidence: number | null
  server_id: string | null
  /** Nome della macchina (istanza) a cui la risorsa appartiene, quando noto. */
  server_label?: string | null
  host_id: string | null
  zone: string | null
  provider: string | null
  region: string | null
  created_at: string
  discovered_at: string
  state_since: string | null  // When resource entered current state
  quantity?: number | null     // Resource value from ResourcesDiscovery
  unit?: string | null         // Unit type (e.g., 'GB', 'count', 'MB')
  tenant_id?: string | null
  /**
   * How the resource reached the service it is assigned to. Set only where
   * the rows come from assignments; absent when listing bare resources.
   */
  assignment_type?: AssignmentType | string | null
  /** Solo sulle righe che vengono da un'assegnazione: servono a escluderla dalla fatturazione. */
  assignment_id?: string
  fatturabilita?: Fatturabilita
  /** Cessazione programmata della singola risorsa. */
  disdetta?: Disdetta | null
  /** Licenze su richiesta agganciate a mano, in vigore questo mese. */
  articoli_aggiuntivi?: string[]
  /** Gli altri servizi su cui sta la risorsa, se condivisa. */
  altri_servizi?: string[]
}

export interface AssignmentByResourceResponse {
  service_handle: string
  service_name: string
  assignment_id: string
  assigned_at: string
  assigned_by: string
  resource_state?: string | null
  last_seen_at?: string | null
  state_changed_at?: string | null
  inactive_reason?: string | null
  assignment_type?: string | null
  priority?: number
  confidence_score?: number | null
  active?: boolean
  unassigned_at?: string | null
  assignment_reason?: string | null
}

/** Risorse condivise (OPENSPEC_RISORSE_CONDIVISE §3.2). */
export type RegolaQuota = 'fissa' | 'percentuale' | 'resto'

export interface RigaCondivisione {
  assignment_id: string
  service_handle: string
  contract_code: string | null
  account_id: string | null
  /** null: nessuna quota in vigore (risorsa intera, o condivisione incompleta). */
  regola: RegolaQuota | null
  valore: number | null
}

export interface Condivisione {
  resource_id: string
  /** Tutte le assegnazioni attive, anche quelle che pagano da una data futura. */
  assegnazioni: RigaCondivisione[]
  valida_dal: string | null
  /** Solo con 2+ servizi che pagano oggi e quote complete. Parti per service_handle. */
  anteprima: { totale: number; parti: Record<string, number>; avvisi: string[] } | null
  storico: {
    valida_dal: string
    valida_al: string | null
    assegnazioni: { assignment_id: string; service_handle: string | null; regola: RegolaQuota; valore: number | null }[]
  }[]
  incompleta: boolean
}

/** Una voce del PUT: un'assegnazione esistente o un servizio nuovo. */
export interface VoceCondivisione {
  assignment_id?: string
  service_handle?: string
  regola: RegolaQuota
  valore?: number
}

export interface BulkAssignRequest {
  service_handle: string
  resource_ids: string[]
}

export interface BulkUnassignRequest {
  service_handle: string
  resource_ids: string[]
}

export interface TemplateViolation {
  resource_type: string  // e.g., 'server', 'vcpu'
  message: string  // e.g., "Missing required resource type: server"
  actual_count: number
  required_min: number
  required_max: number | null
}

export interface AssignmentValidationResult {
  valid: boolean
  violations: TemplateViolation[]
}

export interface Ruleset {
  id: string
  ruleset_name: string
  display_name: string
  description: string | null
  priority: number
  config: Record<string, unknown>
  active: boolean
  created_at: string
}

export interface AlertRule {
  id: string
  name: string
  description: string | null
  trigger_conditions: Record<string, unknown>
  severity: string
  notification_channels: string[]
  cooldown_minutes: number
  active: boolean
}

export interface AuditLog {
  log_id: string
  user_id: string | null
  action: string
  resource_type: string
  resource_id: string
  details: Record<string, unknown>
  ip_address: string | null
  timestamp: string
}

export interface ResourceStateHistory {
  history_id: string
  assignment_id: string
  service_handle: string
  resource_id: string
  old_state: ResourceState | null
  new_state: ResourceState
  state_reason: string | null
  detected_at: string
  detected_by: string
  source_system: string | null
  resource_snapshot: Record<string, unknown>
  created_at: string
}

// API Response Types
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

export type ContractStatus = 'draft' | 'active' | 'terminated' | 'expired'

export interface Contract {
  contract_id: string
  /** Codice del gestionale: e' quello che compare negli URL, non l'id. */
  code: string
  account_id: string
  account_name: string | null
  description: string | null
  notes: string | null
  /** I quattro valori della vendita nel gestionale; il rif e' testo libero. */
  commessa: string | null
  prodotto: string | null
  tipo: string | null
  rif: string | null
  status: ContractStatus
  start_date: string | null
  end_date: string | null
  services_count: number
  /** Esclusione manuale dalla fatturazione (OPENSPEC_NON_FATTURABILI). */
  fatturabilita?: Fatturabilita
  disdetta?: Disdetta | null
  version: number
  created: string | null
  updated: string | null
  created_by: string | null
  updated_by: string | null
  deleted_at: string | null
}

export interface ContractCreate {
  code: string
  account_id: string
  description?: string | null
  notes?: string | null
  commessa?: string | null
  prodotto?: string | null
  tipo?: string | null
  rif?: string | null
  status?: ContractStatus
  start_date?: string | null
  end_date?: string | null
}

export type ContractUpdate = Partial<ContractCreate>

export interface ContractResourceSummaryRow {
  resource_type: string
  value_type: string
  count: number
  total_value: number
}

export interface ContractResourceSummary {
  contract_code: string
  total: number
  by_type: ContractResourceSummaryRow[]
  /** L'inventario e' arrivato troncato o non e' arrivato: il totale e' un minimo. */
  partial: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface ApiError {
  detail: string
  status_code: number
}

// Form Types
export interface ServiceCreate {
  name: string
  service_handle?: string
  description?: string
  customer_type?: string
  customer_detail?: string
  /** Lo schema del backend lo accetta da sempre; qui mancava, e senza di esso
   *  un servizio creato da un contratto non supera il controllo di coerenza. */
  account_id?: string
  template_id?: string
  environment?: string
  assignment_strategy?: string
  ruleset_name?: string
  region?: string
  data_center?: string
  cost_center?: string
  contract_id?: string
  codice_prodotto?: string
  request_id?: string
  notes?: string
  tags?: Record<string, unknown>
}

export interface ServiceUpdate {
  name?: string
  description?: string
  customer_type?: string
  customer_detail?: string
  account_id?: string
  environment?: string
  region?: string
  data_center?: string
  cost_center?: string
  contract_id?: string
  /** null = svuota, assente = non toccarlo. */
  codice_prodotto?: string | null
  request_id?: string
  notes?: string
  assignment_strategy?: string
  ruleset_name?: string
  tags?: Record<string, unknown>
  status?: string
}

export interface Prodotto {
  codice: string
  descrizione: string
}

export interface AccountCreate {
  name: string
  type?: string
  legal_name?: string
  cost_center?: string
  region?: string
  contact_email?: string
  contact_name?: string
  notes?: string
  piva?: string
  vat_number?: string
  codice_fiscale?: string
  fiscal_code?: string
  commercial_ics?: string
  commercial_bsa?: string
  azienda_riferimento?: string
  ref_applicativo?: string
}

export type CustomerCreate = AccountCreate;

export interface AccountUpdate {
  name?: string
  type?: string
  legal_name?: string
  cost_center?: string
  region?: string
  contact_email?: string
  contact_name?: string
  notes?: string
  active?: boolean
  piva?: string
  vat_number?: string
  codice_fiscale?: string
  fiscal_code?: string
  commercial_ics?: string
  commercial_bsa?: string
  azienda_riferimento?: string
  ref_applicativo?: string
}

export type CustomerUpdate = AccountUpdate;

export interface TemplateCreate {
  name: string
  description?: string
  version?: string // Optional for create (defaults to 1.0)
  resource_types: TemplateResourceTypeCreate[]
}

export interface TemplateResourceTypeCreate {
  resource_type_code: string
  mandatory: boolean
  min_quantity: number
  max_quantity: number
}

export interface TemplateUpdate {
  name?: string
  description?: string
  version: string // Required for edit (auto-incremented)
  resource_types?: TemplateResourceTypeCreate[]
}

export interface TemplateDependencyResponse {
  services: Array<{
    name: string
    service_handle: string
  }>
}

// Resource Discovery API Types (Phase 25)

/**
 * Resource from ResourcesDiscovery API
 * Mirrors the Resource.to_dict() output for list view
 */
export interface ResourceFromAPI {
  resource_id: string
  server_id: string
  host_id: string | null
  provider_resource_id?: string | null
  name: string
  resource_type: string
  provider: string
  value: number
  value_type: string
  tenant_id: string | null
  project_id: string | null
  billing_id: string | null
  domain: string | null
  url: string | null
  ip_address: string | null
  ip_address2?: string | null
  ip_address3?: string | null
  /** Scritti a mano in ServiceManager: la raccolta dati non li tocca. */
  campi_aggiuntivi?: CampoRisorsa[]
  os_type?: string | null
  os_version?: string | null
  owner?: string | null
  owner_id?: string | null
  region: string | null
  zone: string | null
  status: string
  environment: string | null
  description: string | null
  tags: Record<string, unknown> | null
  options?: Array<{ code: string; name: string; status?: string; category?: 'commercial' | 'technical' }> | Record<string, any> | null
  info_1?: string | Record<string, unknown> | null
  info_2?: string | Record<string, unknown> | null
  info_3?: string | Record<string, unknown> | null
  info_4?: string | Record<string, unknown> | null
  info_5?: string | Record<string, unknown> | null
  expiry_date?: string | null
  auto_renew?: boolean | null
  version: number
  discovered_at: string
  created: string
  updated: string
  last_checked_at: string | null
  deleted_at: string | null
  first_missing_at?: string | null
  missing_consecutive_runs?: number | null
  created_by?: string | null
  updated_by?: string | null
  changed_by?: string | null
}

/**
 * Response from ResourcesDiscovery GET /api/resources endpoint
 */
export interface ResourceListResponse {
  facets?: { providers: Facet[]; types: Facet[]; statuses: Facet[] }
  resources: ResourceFromAPI[]
  total: number
  page: number
  page_size: number
}

/**
 * Query parameters for ResourcesDiscovery API calls
 */
/** One distinct value of a filterable column, with how many rows carry it. */
export interface Facet {
  value: string
  count: number
}

export interface ResourceQueryParams {
  server_id?: string
  resource_type?: string
  provider?: string
  status?: string
  tenant_id?: string
  /** Comma-separated asset ids, as the asset cards link them. */
  asset_ids?: string
  /** 'assigned' | 'unassigned': ServiceManager joins its assignments in. */
  assignment?: string
  search?: string
  limit?: number
  offset?: number
  sort_by?: string
  order?: 'asc' | 'desc'
}

export interface ResourceCreatePayload {
  name: string
  resource_type: string
  value: number
  value_type: string
  provider?: string
  server_id?: string | null
  host_id?: string | null
  ip_address?: string | null
  url?: string | null
  environment?: string | null
  region?: string | null
  zone?: string | null
  domain?: string | null
  tenant_id?: string | null
  project_id?: string | null
  billing_id?: string | null
  description?: string | null
  status?: string
}

export interface ResourceUpdatePayload {
  name?: string
  resource_type?: string
  value?: number
  value_type?: string
  server_id?: string | null
  host_id?: string | null
  ip_address?: string | null
  url?: string | null
  environment?: string | null
  region?: string | null
  zone?: string | null
  domain?: string | null
  tenant_id?: string | null
  project_id?: string | null
  billing_id?: string | null
  description?: string | null
  status?: string
}

// ===========================================================================
// Billing infragruppo
// ===========================================================================

/** Cadenza dell'articolo. Solo `mensile` entra nella generazione mensile. */
export type PriceType = 'mensile' | 'annuale' | 'una_tantum'

/**
 * Come l'articolo decide di appartenere a un mese.
 * `canone`: la soglia dei 15 giorni. `passante`: il mese e' quello vero,
 * perche' il numero lo fa il fornitore.
 */
export type BillingModel = 'canone' | 'passante'

/** Quale pezzo di una licenza Bitdefender l'articolo prezza. */
export type Pacchetto = 'bundle' | 'core' | 'ats' | 'edr'

export interface Listino {
  listino_id: string
  nome: string
  catalogo_id: string
  catalogo_nome: string
  /** I tipi cliente (customer, intercompany, internal) di cui e' il default. */
  predefinito_per: string[]
  updated_at: string | null
  updated_by: string | null
  /** Cosa lo usa: tutto a zero, si puo' eliminare. Solo nell'elenco. */
  in_uso?: { contratti: number; default: string[]; righe_fatturazione: number } | null
}

export type TipoCliente = 'customer' | 'intercompany' | 'internal'

/** Un catalogo: gli articoli che uno o piu' listini prezzano. */
export interface Catalogo {
  catalogo_id: string
  nome: string
  /** I nomi dei listini che lo usano; vuoto = si puo' eliminare. */
  listini: string[]
  n_articoli: number
  n_esclusioni: number
  updated_at?: string | null
  updated_by?: string | null
}

/** Un tipo di risorsa che il catalogo decide di non fatturare. */
/** Cosa contiene un articolo composto, per tipo di risorsa: `{ vcpu: 0.08, ram: 16 }`. */
export type Nominali = Record<string, number>

export interface Esclusione {
  /** `*` = ogni ambito. */
  ambito: string
  resource_type_code: string
  motivo: string
  created_at: string
  created_by: string
}

/** Una regola di incluso per licenza (OPENSPEC_INCLUSO_PER_LICENZA.md):
 *  ogni unita' della licenza dello stesso cliente include `per_licenza` del tipo che consuma. */
export interface InclusoLicenza {
  id: number
  catalogo_id: string
  resource_type_code: string
  licenza_resource_type_code: string
  /** Nelle unita' grezze del tipo che consuma (GB). */
  per_licenza: string
  dal: string | null
  al: string | null
  nota: string | null
  consumo: { nome: string | null; unita: string | null }
  licenza: { nome: string | null; unita: string | null }
  created_at: string | null
  created_by: string
  updated_at: string | null
  updated_by: string
}

export interface InclusoLicenzaIn {
  resource_type_code: string
  licenza_resource_type_code: string
  per_licenza: string
  dal: string | null
  al: string | null
  nota: string | null
}

/** Un tipo di risorsa dei contratti del catalogo senza articolo ne' esclusione. */
export interface VoceCopertura {
  ambito: string | null
  resource_type_code: string
  n_risorse: number
}

/** Il default di ogni tipo cliente; null dove non c'e'. */
export type PredefinitiListino = Record<TipoCliente, { listino_id: string; nome: string } | null> & {
  /** Come si fattura ogni tipo cliente. */
  modalita?: Record<TipoCliente, Modalita>
}

export type Modalita = 'soglia_15' | 'giornaliera'

/** Il perimetro di un mese di fatturazione. */
export type Perimetro = 'infragruppo' | 'clienti'

export interface ContrattoDelPeriodo {
  contract_code: string | null
  cliente: string | null
  account_id: string | null
  account_nome: string | null
  line_count: number
  total_amount: string
}

/** Risposta di PUT /price-lists/defaults: cambiare un default ricalcola ogni
 *  contratto di quel tipo senza listino suo, quindi dice quali mesi in bozza tocca. */
export interface PredefinitiAggiornati extends PredefinitiListino {
  periodi_da_rigenerare: string[]
}

export interface PrezzoListino {
  id: number
  listino_id: string
  articolo: string
  prezzo: string
  /** YYYY-MM; null = da sempre. */
  valido_dal: string | null
  /** Calcolato dal backend: il mese prima del prezzo successivo. null = in vigore. */
  valido_al: string | null
  created_at: string | null
  created_by: string | null
}

export interface CondizioneContratto {
  id: number
  contract_id: string
  articolo: string | null
  ambito: string | null
  valido_dal: string | null
  valido_al: string | null
  prezzo: string | null
  sconto_pct: string | null
  block_size: string | null
  round_up: boolean | null
  quantita_fissa: string | null
  modello: BillingModel | null
  note: string | null
  /** Unita' dell'articolo bersaglio ("GB", "unità"); null su ambito e contratto. */
  unita?: string | null
  /** L'articolo non e' nel catalogo del contratto: la condizione non si applica. */
  orfana?: boolean
  updated_at: string | null
  updated_by: string | null
}

export interface CondizioniDelContratto {
  /** Null se non c'e' nessun listino. */
  listino_id: string | null
  listino_nome: string | null
  /** Da dove viene il listino: scelto sul contratto, default del tipo cliente, nessuno. */
  origine: 'contratto' | 'tipo_cliente' | 'nessuno'
  tipo_cliente: string | null
  condizioni: CondizioneContratto[]
  modalita?: Modalita
  modalita_origine?: 'contratto' | 'tipo_cliente' | 'predefinita'
}

export interface ListinoAssegnato {
  listino_id: string | null
  listino_nome: string | null
  origine: 'contratto' | 'tipo_cliente' | 'nessuno'
  tipo_cliente: string | null
  /** Il listino di un contratto non ha date: tocca ogni mese ancora in bozza. */
  periodi_da_rigenerare: string[]
  /** Condizioni d'articolo che il catalogo del nuovo listino non conosce. */
  condizioni_orfane?: { id: number; articolo: string }[]
}

export interface CondizioneSalvata {
  condizione: CondizioneContratto
  periodi_da_rigenerare: string[]
}

export interface PriceListItem {
  articolo: string
  ambito: string
  descrizione: string
  prezzo: string | null
  /** L'unita' in cui conta l'articolo ("GB", "unità"); null se non nasce da un tipo di risorsa. */
  unita?: string | null
  /** Il primo prezzo gia' registrato che decorre dopo il mese richiesto. */
  prossimo_prezzo?: { prezzo: string; valido_dal: string } | null
  tipo: PriceType
  modello: BillingModel
  resource_type_code: string | null
  os_match: string | null
  pacchetto: Pacchetto | null
  /** Non nasce da sola dalla risorsa: si aggancia a mano (SQL Server sulle vCPU). */
  su_richiesta?: boolean
  quantita_fissa: string | null
  block_size: string
  round_up: boolean
  attivo: boolean
  nominali?: Nominali | null
  /** Vero se la riga nasce da una risorsa, falso se si aggiunge a mano. */
  automatico: boolean
  /** La convenzione di conteggio gia' resa in parole dal backend. */
  conteggio: string
  updated_at: string | null
  updated_by: string | null
  /** Periodi in draft che il nuovo prezzo tocca: vanno rigenerati. */
  periodi_da_rigenerare?: string[]
}

export interface PriceListResponse {
  total: number
  items: PriceListItem[]
}

export type PeriodStatus = 'draft' | 'closed'

export interface AccountTotal {
  account_id: string
  company: string | null
  line_count: number
  total_amount: string
}

export interface ArticoloTotal {
  articolo: string
  descrizione: string
  line_count: number
  quantity: string
  total_amount: string
}

export interface RisorsaSenzaOS {
  service_handle: string
  resource_id: string
  resource_name: string | null
  provider: string | null
}

/** Un'assegnazione in perimetro che non ha prodotto righe, e il perché. */
export interface Scarto {
  motivo: string
  resource_id: string
  resource_name: string | null
  service_handle: string
  provider: string | null
  /** Quasi sempre sono la risposta: "attivata il 16" spiega gia' lo scarto. */
  attivazione?: string | null
  cessazione?: string | null
  /** Solo su `escluso_manualmente`: chi ha escluso, perche', per quali mesi. */
  livello?: 'contratto' | 'servizio' | 'risorsa' | null
  nota?: string | null
  dal?: string | null
  al?: string | null
}

export interface ComposizioneIncongruente {
  service_handle: string
  macchina: string | null
  server_id: string
  articoli: string[]
  scostamenti: { tipo: string; misurato: string; venduto: string }[]
}

export interface PeriodSummary {
  period: string
  quarter: string
  status: PeriodStatus
  line_count: number
  total_amount: string
  generated_at: string | null
  generated_by: string | null
  closed_at: string | null
  closed_by: string | null
  by_account: AccountTotal[]
  by_articolo: ArticoloTotal[]
  /** Quante assegnazioni sono state saltate, e perche'. Derivato da `scarti`. */
  skipped: Record<string, number>
  /** Le assegnazioni saltate, una per una: il conteggio da solo non dice quali. */
  scarti: Scarto[]
  os_sconosciuto: RisorsaSenzaOS[]
  /** Macchine vendute a composizione che misurano piu' del venduto: avviso. */
  composizioni_incongruenti?: ComposizioneIncongruente[]
  /** Righe con la quantita' ereditata da oggi invece che ricostruita. */
  righe_stimate: number
  quantita_a_fine_periodo: boolean
  perimetro?: Perimetro
  /** Cio' che il motore non ha saputo fatturare. Il mese si genera lo stesso. */
  anomalie?: Anomalie
  /** Chi ha chiuso il mese sapendo delle anomalie. */
  chiuso_con_anomalie?: ChiusoConAnomalie | null
  /** Quote delle risorse condivise da guardare: non fermano la chiusura. */
  avvisi?: AvvisoQuota[]
}

export interface AvvisoQuota {
  avviso: 'quote_oltre_il_totale' | 'quota_non_assegnata' | 'modelli_diversi' | string
  resource_id: string
  resource_name: string | null
  giorno: string | null
}

export interface Anomalie {
  servizi_senza_account: { service_handle: string; name: string | null }[]
  tipi_senza_articolo: { catalogo: string; ambito: string; resource_type_code: string; risorse: number }[]
  contratti_senza_listino: { service_handle: string; contract_code: string | null; tipo_cliente: string | null }[]
  articoli_non_prezzati: { listino: string; articolo: string }[]
  agganci_non_applicabili: { service_handle: string; resource_name: string | null; articolo: string; catalogo: string }[]
  /** Risorse su 2+ servizi con quote che non reggono: nessuna riga finche' non si sistemano. */
  condivisioni_incomplete: { resource_id: string; resource_name: string | null }[]
  /** Risorse intere rimaste fuori dal mese. */
  risorse_saltate: number
}

export interface ChiusoConAnomalie {
  da: string
  il: string
  risorse_saltate: number
}

export interface PeriodListItem {
  period: string
  quarter: string
  status: PeriodStatus
  line_count: number
  total_amount: string
  generated_at: string | null
  closed_at: string | null
  perimetro?: Perimetro
}

export interface BillingLine {
  line_id: string
  period: string
  account_id: string
  company: string | null
  service_handle: string
  service_name: string
  commessa: string | null
  contract_code: string | null
  resource_id: string
  resource_name: string | null
  provider: string
  resource_type_code: string
  ambito: string
  articolo: string
  descrizione: string
  /** Il valore nativo letto: spiega perche' 130 GB fanno 2 blocchi. */
  raw_quantity: string
  raw_unit: string | null
  quantity: string
  unit_price: string
  amount: string
  billing_start: string
  quantita_stimata: boolean
  /** La quota di una risorsa condivisa: "50%", "fissa 50", "resto", "intera"; null se non condivisa. */
  quota?: string | null
  /** La derivazione dell'importo, congelata alla generazione. `null` sulle
   *  righe generate prima del log del calcolo. */
  calcolo?: Record<string, unknown> | null
  /** La stessa derivazione in italiano, un passo per frase. */
  spiegazione?: string[]
}

export interface QuarterAccountTotal {
  account_id: string
  company: string | null
  per_mese: Record<string, string>
  total_amount: string
}

export interface QuarterSummary {
  quarter: string
  months: string[]
  months_generated: string[]
  months_closed: string[]
  status: PeriodStatus
  line_count: number
  total_amount: string
  by_account: QuarterAccountTotal[]
  /** Mesi generati con anomalie: per chiudere il trimestre serve una conferma. */
  months_with_anomalies?: string[]
}

export interface Variazione {
  classe: 'entrata' | 'uscita' | 'variata'
  account_id: string
  company: string | null
  service_handle: string
  resource_name: string | null
  articolo: string
  descrizione: string
  quantity: string
  previous_quantity: string
  amount: string
  previous_amount: string
  delta_amount: string
  /** Assenti dove la riga non c'era: un'entrata non ha un prezzo di prima. */
  unit_price?: string | null
  previous_unit_price?: string | null
}

export interface ChangesResponse {
  period: string
  previous_period: string
  /** Falso se il mese prima non e' mai stato generato: niente confronto. */
  previous_generated?: boolean
  total_amount: string
  previous_total_amount: string
  delta_amount: string
  /** Falso se la somma dei delta non torna: il report starebbe mentendo. */
  quadra: boolean
  entrate: Variazione[]
  uscite: Variazione[]
  variate: Variazione[]
}

/** Un campo scritto a mano su una risorsa: l'IP di una LPAR, un hostname (OPENSPEC_CAMPI_RISORSA). */
export interface CampoRisorsa {
  id: number
  resource_id: string
  campo: string
  valore: string
  nota: string | null
  updated_at: string | null
  updated_by: string
}

/** Un articolo su richiesta agganciato a mano a una risorsa (OPENSPEC_ARTICOLI_SU_RICHIESTA). */
export interface Aggancio {
  id: number
  articolo: string
  descrizione: string | null
  dal: string
  al: string | null
  nota: string | null
  /** Vuota = la quantita' delle regole dell'articolo. */
  quantita?: number | null
  /** Vincono su quelli dell'articolo (i `CUSTOM`). */
  nominali?: Nominali | null
  created_by: string
  /** Falso se il catalogo del contratto attuale non ha l'articolo; null senza servizio. */
  nel_catalogo_attuale: boolean | null
}

/** Quanto di un articolo il servizio ha gia' pagato (OPENSPEC_QUANTITA_INCLUSE). */
export interface QuantitaInclusa {
  id: number
  service_handle: string
  articolo: string
  /** Nelle unita' dell'articolo, dopo il blocco. */
  quantita: string
  dal: string | null
  al: string | null
  nota: string | null
  created_by: string
  descrizione: string | null
  /** "1 unità di listino ogni 1024 GB, arrotondata per eccesso". */
  conteggio: string | null
  /** Per leggere 2 come 200 GB; null con quantità fissa o senza tipo di risorsa. */
  block_size: string | null
  unita: string | null
}
